import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useAuthContext } from "./AuthContext"
import { USER_TYPES, LOCAL_STORAGE_PREFIX } from "common/constants"
import useApi from "modules/professor/account"
import { getSelectedGroup, getProfessorGroups } from "modules/professor/groups"
import { useThemeContext } from "./ThemeContext"
import {
  bgOptions,
  fontFamilyOptions,
  fontOptions,
} from "common/textEditorConstants"
import storage from "utils/persistentStorage"

export const TeacherAuthContext = createContext()

export const useTeacherAuthContext = () => {
  const context = useContext(TeacherAuthContext)
  if (context === undefined) {
    throw new Error(
      "useTeacherAuthContext must be used within a TeacherAuthProvider"
    )
  }
  return context
}

export const TeacherAuthProvider = ({ children }) => {
  const {
    userData,
    updateUserData: baseUpdateUserData,
    logout: baseLogout,
  } = useAuthContext()
  const { getUser } = useApi()
  const [selectedGroupData, setSelectedGroupData] = useState({
    groupId: -1,
    teachers: [],
    isSequentialContent: false,
  })
  const [textEditorSettings, setTextEditorSettings] = useState({
    fontSize: 1,
    fontFamily: 0,
    backgroundColor: 0,
    fillColor: 0,
    ruleEnabled: true,
  })
  const [isConfigurationCompleted, setIsConfigurationCompleted] =
    useState(false)
  const [isRestoreCompleted, setIsRestoreCompleted] = useState(false)
  const isRestoring = useRef(false)
  const { setFontFamily, setFontSize, setBgColor, setMode } = useThemeContext()

  useEffect(() => {
    if (!userData || userData.userId <= 0) {
      setSelectedGroupData({
        groupId: -1,
        teachers: [],
        isSequentialContent: false,
      })
      setTextEditorSettings({
        fontSize: 1,
        fontFamily: 0,
        backgroundColor: 0,
        fillColor: 0,
        ruleEnabled: true,
      })
      setIsConfigurationCompleted(false)
      setIsRestoreCompleted(false)
      isRestoring.current = false
      return
    }
    if (userData.userTypeId === USER_TYPES.TEACHER) {
      setIsConfigurationCompleted(
        userData?.isConfigurationCompleted && !userData?.passwordUpdateRequired
      )
      if (isRestoreCompleted || isRestoring.current) {
        return
      }
      isRestoring.current = true

      // Restore text editor settings
      localStorage.setItem("smash-prefers-dark-mode", "false")
      document.documentElement.classList.remove("dark-mode")
      setMode("light")
      setBgColor("white")
      setFontFamily("Poppins Regular")
      setFontSize("18px")

      if (userData.textEditorSettings) {
        setTextEditorSettings(userData.textEditorSettings)
        if (userData.textEditorSettings.fontFamily >= 0) {
          const selectedFontStyle =
            fontFamilyOptions[userData.textEditorSettings.fontFamily]
          const selectedFontSize =
            fontOptions[userData.textEditorSettings.fontSize]
          const selectedBgColor =
            bgOptions[userData.textEditorSettings.backgroundColor]
          setBgColor(selectedBgColor?.bg || "white")
          setFontFamily(selectedFontStyle?.name || "Poppins Regular")
          setFontSize(selectedFontSize?.fontSize || "18px")
        }
      }

      const checkSession = async () => {
        const freshUser = await getUser()
        if (freshUser) {
          baseUpdateUserData({ newUserData: freshUser })
        } else {
          isRestoring.current = false
          logout()
          return
        }
        const selectedGroup = await getSelectedGroup()
        if (selectedGroup) {
          setSelectedGroupData(selectedGroup)
        } else {
          // get all groups and select first
          const groups = await getProfessorGroups({ sortBy: "Sequence" })
          if (groups?.data?.items?.length > 0) {
            const firstGroup = groups.data.items[0]
            setSelectedGroupData(firstGroup)
          }
        }
        setIsRestoreCompleted(true)
        isRestoring.current = false
      }
      checkSession()
    }
  }, [userData, isRestoreCompleted])

  const logout = useCallback(() => {
    setSelectedGroupData({
      groupId: -1,
      teachers: [],
      isSequentialContent: false,
    })
    setTextEditorSettings({
      fontSize: 1,
      fontFamily: 0,
      backgroundColor: 0,
      fillColor: 0,
      ruleEnabled: true,
    })
    baseLogout()
  }, [baseLogout])

  const updateSelectedGroupData = useCallback(
    (newGroupData) => {
      setSelectedGroupData({
        ...selectedGroupData,
        ...newGroupData,
      })
    },
    [selectedGroupData]
  )

  const updateTextSettings = useCallback(
    (settings) => {
      setTextEditorSettings(settings)
      if (settings?.fontFamily >= 0) {
        const selectedFontStyle = fontFamilyOptions[settings.fontFamily]
        const selectedFontSize = fontOptions[settings.fontSize]
        const selectedBgColor = bgOptions[settings.backgroundColor]
        setBgColor(selectedBgColor?.bg || "white")
        setFontFamily(selectedFontStyle?.name || "Poppins Regular")
        setFontSize(selectedFontSize?.fontSize || "18px")
      }
      const userId = userData?.userId
      if (!userId || userId <= 0) return
      storage
        .getItem(`${LOCAL_STORAGE_PREFIX}userAll_${userId}`)
        .then((user) => {
          if (user) {
            user.textEditorSettings = settings
            storage.setItem(`${LOCAL_STORAGE_PREFIX}userAll_${userId}`, user)
          }
        })
    },
    [userData]
  )

  const values = useMemo(
    () => ({
      selectedGroupData,
      isConfigurationCompleted,
      isRestoreCompleted,
      setIsConfigurationCompleted,
      logout,
      setSelectedGroupData: updateSelectedGroupData,
      textEditorSettings,
      setTextEditorSettings: updateTextSettings,
    }),
    [
      selectedGroupData,
      isConfigurationCompleted,
      isRestoreCompleted,
      textEditorSettings,
      updateTextSettings,
      updateSelectedGroupData,
      logout,
    ]
  )

  return (
    <TeacherAuthContext.Provider value={values}>
      {children}
    </TeacherAuthContext.Provider>
  )
}

export default TeacherAuthProvider
